import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TimeSelect from './TimeSelect';
import { findUnresolvedConflicts } from '../utils/scheduleConflict';

const API = 'https://api.cpms-srlkevcharge.com';

const DAYS = [
  { key: 'Mon', label: 'Mon' }, { key: 'Tue', label: 'Tue' }, { key: 'Wed', label: 'Wed' },
  { key: 'Thu', label: 'Thu' }, { key: 'Fri', label: 'Fri' }, { key: 'Sat', label: 'Sat' }, { key: 'Sun', label: 'Sun' },
];

const IconChevron = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const IconCheck = () => (
  <svg width="15" height="15" fill="none" stroke="var(--srlk-green)" strokeWidth="3" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
);
const IconAlert = () => (
  <svg width="15" height="15" fill="none" stroke="var(--red)" strokeWidth="2.5" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const IconTrash = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

function DayPicker({ value, onChange }) {
  const selectedDays = value ? value.split(',').map(d => d.trim()) : [];
  const toggleDay = (dayKey) => {
    const isSelected = selectedDays.includes(dayKey);
    const updated = isSelected ? selectedDays.filter(d => d !== dayKey) : [...selectedDays, dayKey];
    onChange(updated.length > 0 ? updated.join(',') : '');
  };
  return (
    <div className="day-picker">
      {DAYS.map(day => (
        <button type="button" key={day.key} className={`day-pill ${selectedDays.includes(day.key) ? 'selected' : ''}`} onClick={() => toggleDay(day.key)} title={day.key}>
          {day.label}
        </button>
      ))}
    </div>
  );
}

function CardTariffMatchEditor({ match, onUpdate, onRemove, isInConflict }) {
  const [showDays, setShowDays] = useState(!!match.days_of_week);
  const [showDates, setShowDates] = useState(!!(match.effective_start_date || match.effective_end_date));

  return (
    <div className={`bulk-tariff-match-block ${isInConflict ? 'has-conflict' : ''}`}>
      <div className="bulk-tariff-match-row">
        <div className={`bulk-tariff-match-name ${match.found ? '' : 'not-found'}`}>
          {match.tariff_name}
          {!match.found && <span className="bulk-tariff-not-found-tag">Not found</span>}
        </div>
        {match.found ? (
          <>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Start Time</label>
              <TimeSelect value={match.start_time || ''} onChange={(val) => onUpdate('start_time', val)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11 }}>End Time</label>
                <TimeSelect value={match.end_time || ''} onChange={(val) => onUpdate('end_time', val)} />
              </div>
              <button type="button" className="tariff-schedule-remove" onClick={onRemove} title="Remove this tariff">
                <IconTrash />
              </button>
            </div>
          </>
        ) : (
          <div style={{ gridColumn: 'span 2', fontSize: 12, color: 'var(--text-muted)' }}>
            This tariff name does not match any existing tariff and will be skipped.
          </div>
        )}
      </div>

      {match.found && (
        <>
          <div className="advanced-toggle-row" style={{ marginTop: 10, paddingTop: 10 }}>
            <label className="advanced-toggle">
              <input type="checkbox" checked={showDays} onChange={() => setShowDays(p => !p)} />
              Specific days
            </label>
            <label className="advanced-toggle">
              <input type="checkbox" checked={showDates} onChange={() => setShowDates(p => !p)} />
              Specific date range
            </label>
          </div>

          {showDays && (
            <div className="advanced-section">
              <DayPicker value={match.days_of_week} onChange={(val) => onUpdate('days_of_week', val)} />
            </div>
          )}

          {showDates && (
            <div className="advanced-section">
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" value={match.effective_start_date || ''} onChange={(e) => onUpdate('effective_start_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" value={match.effective_end_date || ''} onChange={(e) => onUpdate('effective_end_date', e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function BulkCardUploadReview({ cards, onUpdateCard, onRemoveCard }) {
  const [expanded, setExpanded] = useState(Object.fromEntries(cards.map((c, i) => [i, i === 0])));
  const [allTariffs, setAllTariffs] = useState([]);
  const [tariffLookup, setTariffLookup] = useState({});
  const [addingTariffFor, setAddingTariffFor] = useState(null);
  const [newTariffSelection, setNewTariffSelection] = useState({ tariff_id: '', start_time: '', end_time: '' });

  useEffect(() => {
    axios.get(`${API}/tariffs/`).then(res => {
      setAllTariffs(res.data);
      const lookup = {};
      res.data.forEach(t => { lookup[t.id] = t.name; });
      setTariffLookup(lookup);
    }).catch(() => {});
  }, []);

  const toggle = (index) => setExpanded(prev => ({ ...prev, [index]: !prev[index] }));

  const updateField = (index, field, value) => {
    const updated = { ...cards[index], [field]: value };
    onUpdateCard(index, updated);
  };

  const updateTariffMatchField = (cardIndex, matchTempId, field, value) => {
    const updated = { ...cards[cardIndex] };
    updated.tariff_matches = updated.tariff_matches.map(m => m.tempId === matchTempId ? { ...m, [field]: value } : m);
    onUpdateCard(cardIndex, updated);
  };

  const removeTariffMatch = (cardIndex, matchTempId) => {
    const updated = { ...cards[cardIndex] };
    updated.tariff_matches = updated.tariff_matches.filter(m => m.tempId !== matchTempId);
    onUpdateCard(cardIndex, updated);
  };

  const resolveConflict = (cardIndex, tempIdWinner) => {
    const updated = { ...cards[cardIndex] };
    updated.tariff_matches = updated.tariff_matches.map(m => m.tempId === tempIdWinner ? { ...m, priority: (m.priority || 0) + 1 } : m);
    onUpdateCard(cardIndex, updated);
  };

  const addTariffMatch = (cardIndex) => {
    if (!newTariffSelection.tariff_id) return;
    const tariff = allTariffs.find(t => String(t.id) === String(newTariffSelection.tariff_id));
    if (!tariff) return;

    const updated = { ...cards[cardIndex] };
    const existingNames = (updated.tariff_matches || []).map(m => m.tariff_name);
    if (existingNames.includes(tariff.name)) {
      setAddingTariffFor(null);
      setNewTariffSelection({ tariff_id: '', start_time: '', end_time: '' });
      return;
    }

    updated.tariff_matches = [
      ...(updated.tariff_matches || []),
      {
        tempId: `manual-${Date.now()}`,
        tariff_id: tariff.id,
        tariff_name: tariff.name,
        found: true,
        start_time: newTariffSelection.start_time || null,
        end_time: newTariffSelection.end_time || null,
        days_of_week: null,
        effective_start_date: null,
        effective_end_date: null,
        priority: 0,
      }
    ];
    onUpdateCard(cardIndex, updated);
    setAddingTariffFor(null);
    setNewTariffSelection({ tariff_id: '', start_time: '', end_time: '' });
  };

  return (
    <>
      {cards.map((card, index) => {
        const isOpen = !!expanded[index];
        const foundMatches = (card.tariff_matches || []).filter(m => m.found);
        const unresolvedConflicts = foundMatches.length >= 2
          ? findUnresolvedConflicts(foundMatches, tariffLookup).map(c => ({
              ...c, tempIdA: foundMatches[c.indexA].tempId, tempIdB: foundMatches[c.indexB].tempId,
            }))
          : [];
        const isMatchInConflict = (tempId) => unresolvedConflicts.some(c => c.tempIdA === tempId || c.tempIdB === tempId);
        const isFree = card.free_charging === 'Yes';

        return (
          <div key={index} className={`bulk-card-item ${card.valid ? 'valid' : 'invalid'}`}>
            <div className="bulk-card-header" onClick={() => toggle(index)}>
              <div>
                <div className="bulk-card-title">
                  {card.valid ? <IconCheck /> : <IconAlert />}
                  {card.uid}
                </div>
                <div className="bulk-card-meta">
                  {card.organisation_name || 'No organisation'} · {isFree ? 'Free charging' : `${foundMatches.length} tariff${foundMatches.length !== 1 ? 's' : ''} matched`}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <button
                  type="button"
                  className="entity-remove-btn"
                  onClick={(e) => { e.stopPropagation(); onRemoveCard(index); }}
                  title="Remove this card from the upload"
                >
                  <IconTrash />
                </button>
                <IconChevron style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
              </div>
            </div>

            {isOpen && (
              <div className="bulk-card-body">
                {card.errors && card.errors.length > 0 && (
                  <div className="bulk-errors-inline">
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {card.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </div>
                )}

                {card.organisation_warning && (
                  <div className="bulk-errors-inline" style={{ background: '#fef3c7', color: '#92400e' }}>
                    {card.organisation_warning}
                  </div>
                )}

                <div className="bulk-field-grid" style={{ marginBottom: 16 }}>
                  <div className="bulk-field">
                    <label>RFID UID *</label>
                    <input
                      type="text"
                      className={!card.uid ? 'field-error' : ''}
                      value={card.uid || ''}
                      onChange={(e) => updateField(index, 'uid', e.target.value)}
                    />
                  </div>
                  <div className="bulk-field">
                    <label>Organisation Name</label>
                    <input type="text" value={card.organisation_name || ''} onChange={(e) => updateField(index, 'organisation_name', e.target.value)} />
                  </div>
                  <div className="bulk-field">
                    <label>RFID Type</label>
                    <select value={card.rfid_type || 'Prepaid'} onChange={(e) => updateField(index, 'rfid_type', e.target.value)}>
                      <option value="Prepaid">Prepaid</option>
                      <option value="Postpaid">Postpaid</option>
                    </select>
                  </div>
                  <div className="bulk-field">
                    <label>Status</label>
                    <select value={card.status || 'Active'} onChange={(e) => updateField(index, 'status', e.target.value)}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="bulk-field">
                    <label>Free Charging</label>
                    <select value={card.free_charging || 'No'} onChange={(e) => updateField(index, 'free_charging', e.target.value)}>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                  <div className="bulk-field">
                    <label>Description</label>
                    <input type="text" value={card.description || ''} onChange={(e) => updateField(index, 'description', e.target.value)} />
                  </div>
                </div>

                {!isFree && (
                  <div className="bulk-tariff-block">
                    <div className="bulk-tariff-block-title">Tariffs — set hours, days, or dates for each, or leave blank for all times</div>

                    {unresolvedConflicts.map((conflict, i) => (
                      <div className="collision-choice-box" key={i}>
                        <div className="collision-choice-message">{conflict.message}</div>
                        <div className="collision-choice-buttons">
                          <button type="button" className="collision-choice-btn" onClick={() => resolveConflict(index, conflict.tempIdA)}>
                            Give priority to {tariffLookup[foundMatches.find(m => m.tempId === conflict.tempIdA)?.tariff_id]}
                          </button>
                          <button type="button" className="collision-choice-btn" onClick={() => resolveConflict(index, conflict.tempIdB)}>
                            Give priority to {tariffLookup[foundMatches.find(m => m.tempId === conflict.tempIdB)?.tariff_id]}
                          </button>
                        </div>
                      </div>
                    ))}

                    {(!card.tariff_matches || card.tariff_matches.length === 0) && addingTariffFor !== index && (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                        No tariffs assigned. This card will use whatever tariff each station charges.
                      </div>
                    )}

                    {(card.tariff_matches || []).map(match => (
                      <CardTariffMatchEditor
                        key={match.tempId}
                        match={match}
                        isInConflict={isMatchInConflict(match.tempId)}
                        onUpdate={(field, value) => updateTariffMatchField(index, match.tempId, field, value)}
                        onRemove={() => removeTariffMatch(index, match.tempId)}
                      />
                    ))}

                    {addingTariffFor === index ? (
                      <div className="tariff-schedule-form-row" style={{ marginTop: 10, marginBottom: 0 }}>
                        <div className="form-group">
                          <label>Select Tariff</label>
                          <select value={newTariffSelection.tariff_id} onChange={(e) => setNewTariffSelection(prev => ({ ...prev, tariff_id: e.target.value }))}>
                            <option value="">Select...</option>
                            {allTariffs.map(t => <option key={t.id} value={t.id}>{t.name} — {t.organisation_name}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Start Time</label>
                          <TimeSelect value={newTariffSelection.start_time} onChange={(val) => setNewTariffSelection(prev => ({ ...prev, start_time: val }))} />
                        </div>
                        <div className="form-group">
                          <label>End Time</label>
                          <TimeSelect value={newTariffSelection.end_time} onChange={(val) => setNewTariffSelection(prev => ({ ...prev, end_time: val }))} />
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button type="button" className="btn btn-green" style={{ padding: '9px 14px' }} onClick={() => addTariffMatch(index)}>Add</button>
                          <button type="button" className="btn btn-ghost" style={{ padding: '9px 14px' }} onClick={() => { setAddingTariffFor(null); setNewTariffSelection({ tariff_id: '', start_time: '', end_time: '' }); }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="add-entity-btn" style={{ padding: 10, marginBottom: 0, marginTop: 4 }} onClick={() => setAddingTariffFor(index)}>
                        <IconPlus /> Add Tariff
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}